// NotesApp source file: cards.component.ts

import { Input, Output, EventEmitter, Component, OnInit } from '@angular/core';
import { computed, inject, SimpleChanges } from '@angular/core';

import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Observable } from 'rxjs';

// PRIMENG GUI COMPONENTS
import { SelectModule } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { StyleClassModule } from 'primeng/styleclass';
import { MenuModule } from 'primeng/menu';
import { InputTextModule } from 'primeng/inputtext';
import { BlockUIModule } from 'primeng/blockui';
import { DialogModule } from 'primeng/dialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';

// Notes-App Services
import { Note, Category } from '../../../_service/data.service';
import { UserSettingsService } from '../../../_service/user-settings.service';
import { LayoutService } from '../../../_service/layout.service';
import { LogService } from '../../../_service/log.service';
import { FilterParams } from '../../../_service/filter.service';

type CardSort = {
  project_id: number;
  sort_order: number;
};

// Every combination of Note to Catgory, plus flag if it is to be displayed
type Card = {
  _id: string;
  Title: string;
  Type: string;
  CategoryId: string;
  SortOrder: number;
  isActive: boolean; // still needed?
  isExpanded: boolean;
  isVisible: boolean;
  Color?: string;
};

type NoteCard = {
  //?? necessary?
  NoteID: string;
  CardID: string;
  isActive: boolean;
};

@Component({
  selector: 'cards-view',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    RouterModule,
    MenuModule,
    InputTextModule,
    StyleClassModule,
    SelectModule,
    ButtonModule,
    BlockUIModule,
    DialogModule,
    ProgressSpinnerModule,
  ],
  templateUrl: './cards.component.html',
  styleUrls: ['./cards.component.scss'],
  standalone: true,
})
export class CardsComponent implements OnInit {
  @Input() notes: Note[] = [];
  @Input() categories: Category[] = [];
  @Input() filter: FilterParams = {};
  @Input() filterCategories: string[] | undefined;
  @Input() isSelectMode: boolean = false;
  @Input() selectedNoteIds: string[] = [];
  @Output() clickCategoryAction = new EventEmitter<Category>();
  @Output() selectedNoteIdsChange = new EventEmitter<string[]>();

  data: Note[] = [];
  statusValues: string[] | undefined;
  isWorkToggle: boolean = false; // Initialize based on your requirement
  selectedProjectId: number | null = null; // selected project ID for export PDF function
  loadingCounter = 0; // initially count of loading objects in progress
  isOnline: boolean = true; //??


  favoriteNoteIds: string[] = [];
  layoutService: LayoutService = inject(LayoutService);
  isDarkMode = computed(() => this.layoutService.appState().darkMode);

  allNotesByCard: NoteCard[] = [];
  cards: Card[] = [];

  constructor(private router: Router, private logs: LogService, 
    private userSettings: UserSettingsService) {}

  async ngOnInit() {
    console.log('CardsComponent - in ngOnInit(), filter categories:', this.filterCategories);

    // Sort alphabetically by Path (case-insensitive)
    // this.categories.sort((a, b) =>
    //     (a.Path ?? '').toLowerCase().localeCompare((b.Path ?? '').toLowerCase()));

    this.userSettings.userSettings$.subscribe((settings) => {
      if (settings?.primaryColor) {
        this.layoutService.appState.update((state: any) => ({
          ...state,
          primary: settings.primaryColor,
        }));
      }
      if (this.notes?.length) {
        this.buildCards();
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['notes'] && changes['notes'].currentValue) {
      this.buildCards();
    }

    if (changes['categories'] && changes['categories'].currentValue) {
      //   Always resort case-insensitive when categories change
      this.categories.sort((a, b) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));
    }

    console.log('CardsComponent - updated allNotesByCard:', this.allNotesByCard);
  }

  // ##############################################################################
  // PRESENTATION LAYER / GUI ROUTINES (CALL-BACK ETC.)
  // ##############################################################################

  insertCategoryCards(expandedState: Map<string, boolean>, visibleState: Map<string, boolean>): void {
    const activeCategoryIds =
      !this.filterCategories || this.filterCategories.length === 0
        ? null // means: all categories active
        : new Set(this.filterCategories);

    //let sortOrder = this.cards.length;

    for (const category of this.categories) {
      // if user has filtered by category, skip categories that are not active
      const isActive = activeCategoryIds === null || activeCategoryIds.has(category._id);

      if (!isActive) {
        continue;
      }

      let isVisible = false;
      let color = category.Color;
      let sortOrder = category.SortOrder ?? 9999;
      
      // Restore previous visible state if it exists, otherwise calculate
      if (visibleState.has(category._id)) {
        isVisible = visibleState.get(category._id)!;
      } else if (!category.Parent) {
        // top-level categories always visible
        isVisible = true;
      } else {
        // sub-categories visible only if parent category card is expanded
        const parentCard = this.cards.find((c) => c._id === category.Parent);
        if (!color) {
          color = parentCard ? parentCard.Color : undefined;
        }
        isVisible = parentCard ? parentCard.isExpanded : false;
      }

      this.cards.push({
        _id: category._id,
        Title: category.Path,
        Type: 'category',
        CategoryId: category._id, // redundant if main _id is category id
        SortOrder: sortOrder,
        isActive: true,
        isExpanded: expandedState.get(category._id) ?? false,
        isVisible: isVisible,
        Color: color,
      });
    }
    this.cards.sort((a, b) => (a.SortOrder ?? 9999) - (b.SortOrder ?? 9999));
}

  getCardColor(value?: string): string {
    if (!value) {
      return '';
    }
    const trimmed = value.trim();
    if (!trimmed) {
      return '';
    }
    return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
  }

  isCategoryCardVisible(categoryId: string): boolean {
    const category = this.categories.find((c) => c._id === categoryId) ?? null;
    if (!category) {
      return false;
    }

    const card = this.cards.find((c) => c._id === categoryId);

    return card ? card.isVisible : false;
  }

  // TO DO - update below function. Currently just prototype with hard-coded favorites.
  buildCards(): void {
    //const allNotesByCard: NoteCard[] = [];
    // Save the current expanded and visible state before rebuilding
    const expandedState = new Map(this.cards.map(card => [card._id, card.isExpanded]));
    const visibleState = new Map(this.cards.map(card => [card._id, card.isVisible]));
    
    this.cards = [];
    this.allNotesByCard = [];

    // create card Favorites
    const favoritesCard: Card = {
      _id: 'card:favorites',
      Title: 'Favorites',
      Type: 'Favorites', // distinguish from category-based cards
      CategoryId: '', // virtual category
      SortOrder: -2, // ensures it appears at the top
      isActive: true,
      isExpanded: expandedState.get('card:favorites') ?? true,
      isVisible: true,
    };
    this.cards.push(favoritesCard);
    const favoriteIdSet = new Set(this.favoriteNoteIds);
    this.notes.forEach((note) => {
      if (favoriteIdSet.has(note._id)) {
        this.allNotesByCard.push({
          NoteID: note._id,
          CardID: 'card:favorites',
          isActive: true,
        });
      }
    });
    // create card NextActions
    // create card Recent
    const recentCard: Card = {
      _id: 'card:recent',
      Title: 'Recent ⏰',
      Type: 'Recent', // distinguish from category-based cards
      CategoryId: '', // virtual category
      SortOrder: -1, // ensures it appears at the top
      isActive: true,
      isExpanded: expandedState.get('card:recent') ?? true,
      isVisible: true,
    };
    this.cards.push(recentCard);
    // add notes to recentCard that have been modified within last 5 days
    const now = new Date();
    this.notes.forEach((note) => {
      const modified = new Date(note.last_modified);
      const diffTime = Math.abs(now.getTime() - modified.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= 5) {
        this.allNotesByCard.push({
          NoteID: note._id,
          CardID: 'card:recent',
          isActive: true,
        });
      }
    });

    // create card New This Week
    const newThisWeekCard: Card = {
      _id: 'card:new-this-week',
      Title: 'New This Week 🆕',
      Type: 'NewThisWeek',
      CategoryId: '',
      SortOrder: -1,
      isActive: true,
      isExpanded: expandedState.get('card:new-this-week') ?? true,
      isVisible: true,
    };
    this.cards.push(newThisWeekCard);
    // add notes created within the last 7 days
    this.notes.forEach((note) => {
      const created = new Date(note.created_on);
      const diffTime = Math.abs(now.getTime() - created.getTime());
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays <= 7) {
        this.allNotesByCard.push({
          NoteID: note._id,
          CardID: 'card:new-this-week',
          isActive: true,
        });
      }
    });

    this.insertCategoryCards(expandedState, visibleState);
    // create cards based on Categories

    // insert notes into category cards
    this.notes.forEach((note) => {
      // console.log('Processing note:', note._id, note.Categories);
      note.Categories?.forEach((categoryID) => {
        // console.log(' → adding mapping:', note._id, '→', categoryID);
        if (
          !this.filterCategories ||
          (this.filterCategories?.length > 0 && this.filterCategories?.includes(categoryID))
        ) {
          this.allNotesByCard.push({
            NoteID: note._id,
            CardID: categoryID,
            isActive: true,
          });
        } // only if categoryID is not null or empty
      });
    });
    console.log('CardsComponent - updated cards:', this.cards);
  }

  private normalizeFavoriteNoteIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((id) => typeof id === 'string');
  }

  getAttachmentFlag(note: Note): string {
    const attachments = note.Attachments;
    return Array.isArray(attachments) && attachments.length > 0 ? ' 📎' : '';
  }

  getNoteYearPrefix(note: Note): string {
    const now = new Date();
    const modified = new Date(note.last_modified);

    const yearsDiff = now.getFullYear() - modified.getFullYear();
    const monthsDiff = now.getMonth() - modified.getMonth();
    let totalMonths = yearsDiff * 12 + monthsDiff;

    // If current day is less than the modified day, subtract one (not a full month yet)
    if (now.getDate() < modified.getDate()) {
      totalMonths = totalMonths - 1;
    }

    if (totalMonths) {
      return `${totalMonths}m: `;
    } else {
      return '';
    }
  }

  getNotesForCard(cardId: string): Note[] {
    const noteIdSet = new Set(this.allNotesByCard.filter((nc) => nc.CardID === cardId).map((nc) => nc.NoteID));

    return this.notes
      .filter((note) => noteIdSet.has(note._id))
      .sort((a, b) => Date.parse(b.last_modified) - Date.parse(a.last_modified));
  }

  // count the number of filtered notes for a category
  calculateItemCount(cardID: string): number {
    //console.log("Count ",categoryID," = ",
    //  this.allNotesByCategory.filter(rec => rec.CategoryID === categoryID).length);
    return this.allNotesByCard.filter((rec) => rec.CardID === cardID).length;
  }

  clickCardHeader(card: Card): void {
    console.log('clickCardHeader() - Card: ', card.Title);
    this.expandCard(card);
  }

  getCategoryById(categoryId: string) {
    return this.categories.find((c) => c._id === categoryId) ?? null;
  }

  expandCard(clickedCard: Card, event?: MouseEvent): void {
    event?.stopPropagation();
    clickedCard.isExpanded = !clickedCard.isExpanded;
    console.log('expand card clicked', clickedCard.Title, 'isExpanded:', clickedCard.isExpanded);
    // make children visible
    for (const card of this.cards) {
        console.log(' Checking card:', card.Title, 'Type:', card.Type);     
        // Only category cards can be children
      if (card.Type !== 'category') {
        continue;
      }

      const category = this.getCategoryById(card._id);
      if (!category) {
        console.log('Warning - ignoring invalid category ID for card:', card);
        //card.isVisible = false;
        continue;
      }

      // Show card if its category parent matches the expanded card
      if (category.Parent == clickedCard._id) {
        card.isVisible = clickedCard.isExpanded;
      }
    }
  }

  clickNote(note: Note) {
    if (this.isSelectMode) {
      this.toggleSelection(note);
      return;
    }

    console.log('clickNote() - Note: ', note.Title);
    this.router.navigate(['view', note._id], {
      state: { filter: this.filter },
    });
  }

  toggleSelection(note: Note, event?: MouseEvent) {
    event?.stopPropagation();
    console.log('toggleSelection() - Note: ', note.Title);
    const id = note._id;
    if (!id) {
      return;
    }

    const isAlreadySelected = this.isNoteSelected(id);
    const updatedSelection = isAlreadySelected
      ? this.selectedNoteIds.filter((n) => n !== id)
      : [...this.selectedNoteIds, id];

    this.selectedNoteIdsChange.emit(updatedSelection);
  }

  isNoteSelected(id: string | undefined): boolean {
    if (!id) {
      return false;
    }
    return this.selectedNoteIds.includes(id);
  }

  toggleSelectAllForCard(cardId: string, event?: MouseEvent) {
    event?.stopPropagation();
    const noteIds = this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);

    if (noteIds.length === 0) {
      return;
    }

    const allSelected = noteIds.every((id) => this.selectedNoteIds.includes(id));

    const nextSelection = allSelected
      ? this.selectedNoteIds.filter((id) => !noteIds.includes(id))
      : Array.from(new Set([...this.selectedNoteIds, ...noteIds]));

    this.selectedNoteIdsChange.emit(nextSelection);
  }

  areAllNotesSelectedForCard(cardId: string): boolean {
    const noteIds = this.getNotesForCard(cardId)
      .map((note) => note._id)
      .filter((id): id is string => !!id);

    if (noteIds.length === 0) {
      return false;
    }

    return noteIds.every((id) => this.selectedNoteIds.includes(id));
  }

  // ##############################################################################
  // MISC LOGIC FUNCTIONS
  // ##############################################################################
}
